// Error state for failed requests: copy per status (401, 403, 404, 409, 429, 5xx, network), "Try again" and the trace id in mono for support.
import { CircleAlertIcon, LockIcon, SearchXIcon, WifiOffIcon } from "lucide-react";

import { ApiProblem, isApiProblem, problemCopy } from "@/api/problem";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { cn } from "@/lib/cn";

function toProblem(error: unknown): ApiProblem {
  if (isApiProblem(error)) {
    return error;
  }
  return new ApiProblem({
    type: "urn:gridcode:problem:client-error",
    title: "Something went wrong",
    status: 0,
    code: "client-error",
    detail: error instanceof Error ? error.message : null,
    instance: null,
    errors: null,
    trace_id: null,
  });
}

function ProblemIcon({ problem, className }: { problem: ApiProblem; className?: string }) {
  if (problem.status === 401 || problem.status === 403) {
    return <LockIcon aria-hidden className={className} />;
  }
  if (problem.status === 404) {
    return <SearchXIcon aria-hidden className={className} />;
  }
  if (problem.code === "network-error") {
    return <WifiOffIcon aria-hidden className={className} />;
  }
  return <CircleAlertIcon aria-hidden className={className} />;
}

interface ProblemStateProps {
  problem: unknown;
  retry?: () => void;
  compact?: boolean;
  className?: string | undefined;
}

export function ProblemState({ problem: error, retry, compact = false, className }: ProblemStateProps) {
  const problem = toProblem(error);
  const copy = problemCopy(problem);
  if (compact) {
    return (
      <div
        role="alert"
        className={cn(
          "flex items-start gap-2.5 rounded-md bg-verdict-attn-bg px-3 py-2.5 text-ui",
          className,
        )}
      >
        <ProblemIcon problem={problem} className="mt-0.5 size-4 shrink-0 text-verdict-attn-icon" />
        <div className="min-w-0 flex-1">
          <p className="m-0 font-semibold">{copy.title}</p>
          <p className="m-0 text-chip text-ink-2">{copy.description}</p>
          {problem.trace_id ? (
            <p className="m-0 mt-1 font-mono text-label text-ink-2">Trace {problem.trace_id}</p>
          ) : null}
        </div>
        {retry ? (
          <Button variant="outline" size="sm" onClick={retry}>
            Try again
          </Button>
        ) : null}
      </div>
    );
  }
  return (
    <Empty role="alert" className={className}>
      <EmptyHeader>
        <EmptyMedia className="bg-verdict-attn-bg text-verdict-attn-icon">
          <ProblemIcon problem={problem} />
        </EmptyMedia>
        <EmptyTitle>{copy.title}</EmptyTitle>
        <EmptyDescription>{copy.description}</EmptyDescription>
        {problem.trace_id ? (
          <p className="m-0 font-mono text-label text-muted-foreground">
            Trace {problem.trace_id} · {problem.code}
          </p>
        ) : null}
      </EmptyHeader>
      {retry ? (
        <EmptyContent>
          <Button variant="outline" onClick={retry}>
            Try again
          </Button>
        </EmptyContent>
      ) : null}
    </Empty>
  );
}
